import { Component, inject, OnInit, ViewChild } from '@angular/core';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { BehaviorSubject, firstValueFrom, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { StepPanelEnums } from 'src/app/enum/step-panel-enums';
import { TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { RemissionFromOrderDetailModel } from 'src/app/models/rental/remission-from-order-detail.model';
import { RemissionModel } from 'src/app/models/rental/remission.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { RemissionFromOrderService } from 'src/app/services/rental/remission-from-order.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { OrderDataComponent } from '../order-data/order-data.component';

@Component({
  selector: 'app-manual-order',
  templateUrl: './manual-order.component.html',
  styleUrls: ['./manual-order.component.scss'],
  standalone: false
})
export class ManualOrderComponent implements OnInit {

  public readonly config = inject(DynamicDialogConfig);
  private readonly remissionFromOrderService = inject(RemissionFromOrderService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('orderDataModal') orderDataModalComponent?: CustomModalComponent | null = null;

  articleId: number = 0;
  ascendantArticle: boolean = true;
  ascendantStoreStock: boolean = true;
  clearSelectionTrigger: number = 0;
  columnSortArticle: string | null = null;
  columnSortStoreStock: string | null = null;
  componentOrderData = OrderDataComponent;
  contributionState: Map<number, number> = new Map<number, number>();
  currentStep: number = 1;
  dataArticle: PagedResponseModel<RemissionFromOrderDetailModel> =
    new PagedResponseModel<RemissionFromOrderDetailModel>();
  dataStoreStock: PagedResponseModel<RemissionFromOrderDetailModel> =
    new PagedResponseModel<RemissionFromOrderDetailModel>();
  filtersArticle: ColumnResponseModel[] = [];
  filtersStoreStock: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  headerStoreStock: ColumnResponseModel[] = [];
  inputsForOrderDataModal$ = new BehaviorSubject<any>({
    orderId: 0
  });
  limitArticle: number = 100;
  limitStoreStock: number = 100;
  loading: boolean = false;
  orderDetailId: number = 0;
  originalQuantityRemission: Map<number, number> = new Map<number, number>();
  pageArticle: number = 1;
  pageStoreStock: number = 1;
  quotationDetailId: number | null = null;
  quotationId: number | null = null;
  remissionModel: RemissionModel = new RemissionModel();
  selectedArticle: RemissionFromOrderDetailModel | null = null;
  readonly stepPanel = StepPanelEnums;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Datos del pedido',
      icon: 'pi pi-info-circle',
      action: 'order-data'
    }
  ];

  get totalContribution(): number {
    let total = 0;
    for (const quantity of this.contributionState.values()) {
      total += quantity;
    }
    return total;
  }

  get quantityPending(): number {
    return (this.selectedArticle?.quantityPending ?? 0) - this.totalContribution;
  }

  async ngOnInit() {
    const input$ = this.config.data?.inputs$;
    if (!input$) {
      console.error('inputs$ no está definido en config.data');
      return;
    }
    this.subscription = input$?.subscribe((inputs: any) => {
      this.remissionModel = inputs.remissionModel;
    });
    this.loading = true;
    await this.getDataForStep(StepPanelEnums.PanelOne);
    setTimeout(() => this.loading = false, 300);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  applyContributionStateToLoadedItems(items: RemissionFromOrderDetailModel[]) {
    for (const item of items) {
      if (this.contributionState.has(item.id)) {
        const contribution = this.contributionState.get(item.id)!;
        const original = this.originalQuantityRemission.get(item.id) ?? 0;
        item.quantityRemission = original + contribution;
      } else if (item.quantityRemission > 0) {
        this.originalQuantityRemission.set(item.id, item.quantityRemission);
      }
    }
  }

  assignContributionsToNewItems(newItems: RemissionFromOrderDetailModel[]) {
    let available = this.quantityPending;
    if (available <= 0) {
      this.toastHandlerService.showToast(
        'warning',
        `Ya se completó la cantidad pendiente del pedido (${this.quantityPending}).`
      );
      return;
    }

    for (const item of newItems) {
      if (item.stock <= 0 || available <= 0) continue;

      const contribution = Math.min(item.stock, available);
      this.contributionState.set(item.id, contribution);
      available -= contribution;
    }
  }

  async getColumnsArticle() {
    try {
      if (this.headerArticle.length == 0) {
        const response = await firstValueFrom(
          this.remissionFromOrderService.getColumnsArticle()
        );
        this.headerArticle = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para seleccionar artículos del pedido.', error);
    }
  }

  async getColumnsStoresStock() {
    try {
      if (this.headerStoreStock.length == 0) {
        const response = await firstValueFrom(
          this.remissionFromOrderService.getColumnsStoresStock()
        );
        this.headerStoreStock = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas para seleccionar el artículo a remisionar.', error);
    }
  }

  async getDataForStep(step: StepPanelEnums) {
    try {
      const actions: Record<number, () => Promise<any>> = {
        [StepPanelEnums.PanelOne]: () => Promise.all([
          this.getColumnsArticle(),
          this.getOrderArticlesStockForSelection()
        ]),
        [StepPanelEnums.PanelTwo]: () => Promise.all([
          this.getColumnsStoresStock(),
          this.getStoreStockByArticleForSelection()
        ])
      };
      if (actions[step]) {
        await actions[step]();
      }
    } catch (error) {
      console.error(`Error cargando datos del ${StepPanelEnums[step]}:`, error);
    }
  }

  async getOrderArticlesStockForSelection() {
    try {
      const { branchId, costCenterId, remissionId } = this.remissionModel;
      const response = await firstValueFrom(
        this.remissionFromOrderService.getOrderArticlesStockForSelection(
          branchId, costCenterId, remissionId, this.pageArticle, this.limitArticle,
          this.columnSortArticle, this.ascendantArticle, this.filtersArticle
        )
      );
      this.dataArticle = response;
    } catch (error) {
      console.error('Error al obtener los artículos del pedido.', error);
    }
  }

  async getStoreStockByArticleForSelection() {
    try {
      const response = await firstValueFrom(
        this.remissionFromOrderService.getStoreStockByArticleForSelection(
          this.remissionModel.remissionId, this.orderDetailId, this.articleId, this.pageStoreStock,
          this.limitStoreStock, this.columnSortStoreStock, this.ascendantStoreStock, this.filtersStoreStock
        )
      );
      this.applyContributionStateToLoadedItems(response.items ?? []);
      this.dataStoreStock = response;
    } catch (error) {
      console.error('Error al obtener los artículos a remisionar.', error);
    }
  }

  async goToStep(step: StepPanelEnums, activateCallback: (step: number) => void) {
    if (step > StepPanelEnums.PanelOne && this.articleId == 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Por favor, seleccione un artículo válido.'
      );
      return;
    }
    if (step == StepPanelEnums.PanelOne) {
      this.articleId = 0;
    }
    await this.getDataForStep(step);
    this.currentStep = step;
    activateCallback(step);
  }

  onAction(event: { action: string, item?: RemissionFromOrderDetailModel }) {
    switch (event.action) {
      case 'order-data': {
        this.openOrderDataModal(event.item!.orderId);
        break;
      }
    }
  }

  onArticleSelectionChange({ selectedItem }: { selectedItem?: RemissionFromOrderDetailModel }) {
    const { articleId, orderDetailId } = selectedItem ?? {};
    this.orderDetailId = orderDetailId ?? 0;
    this.articleId = articleId ?? 0;
    this.selectedArticle = selectedItem ?? null;
    this.contributionState.clear();
    this.originalQuantityRemission.clear();
    this.clearSelectionTrigger++;
  }

  async onFilterChange(event: { filters: any[] }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    const cleanFilters = this.removeDuplicates(filters);
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.filtersArticle = cleanFilters;
        this.filtersArticle.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelOne));
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.filtersStoreStock = cleanFilters;
        this.filtersStoreStock.forEach(filter => this.processFilter(filter, StepPanelEnums.PanelTwo));
        break;
      }
    }
  }

  async onPageChange(event: { page: number; pageSize: number, sortField: string | undefined, sortOrder: number }) {
    switch (this.currentStep) {
      case StepPanelEnums.PanelOne: {
        this.pageArticle = event.page;
        this.limitArticle = event.pageSize;
        this.columnSortArticle = event.sortField || null;
        this.ascendantArticle = event.sortOrder == 1;
        await this.getOrderArticlesStockForSelection()
        break;
      }
      case StepPanelEnums.PanelTwo: {
        this.pageStoreStock = event.page;
        this.limitStoreStock = event.pageSize;
        this.columnSortStoreStock = event.sortField || null;
        this.ascendantStoreStock = event.sortOrder == 1;
        await this.getStoreStockByArticleForSelection()
        break;
      }
    }
  }

  onStoreStockSelectionChange(event: { selectedItem: RemissionFromOrderDetailModel[] }) {
    if (this.selectedArticle) {
      const selectedItems = event.selectedItem ?? [];
      const selectedIds = new Set(selectedItems.map(item => item.id));
      const currentPageItems = this.dataStoreStock.items ?? [];

      //Revertir registros deseleccionados de la página actual
      this.revertDeselectedItems(currentPageItems, selectedIds);

      //Identificar registros nuevos (seleccionados sin aporte previo)
      const newItems = selectedItems.filter(item => !this.contributionState.has(item.id));

      //Distribuir el pendiente restante entre los nuevos
      this.assignContributionsToNewItems(newItems);

      //Sincronizar quantityRemission en los items visibles
      this.syncPageItemsQuantityRemission(currentPageItems);

      //Forzar change detection en el CustomTableComponent
      this.dataStoreStock = {
        ...this.dataStoreStock,
        items: [...(this.dataStoreStock.items ?? [])]
      };
    }
  }

  openOrderDataModal(orderId: number) {
    this.inputsForOrderDataModal$.next({
      orderId: orderId
    });
    this.orderDataModalComponent?.show();
  }

  processFilter(filter: any, step: StepPanelEnums) {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    const isInvalid = valueFilter == null || valueFilter == undefined || valueFilter == '' || valueFilter == 0;
    if (isInvalid) {
      if (step == StepPanelEnums.PanelOne) {
        this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
      } else if (step == StepPanelEnums.PanelTwo) {
        this.filtersStoreStock = this.filtersStoreStock.filter(f => f.propertyName !== propertyName);
      }
    } else {
      const list = step == StepPanelEnums.PanelOne ? this.filtersArticle : this.filtersStoreStock;
      const existingFilter = list.find(f => f.propertyName == propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index == self.findIndex((t: any) => t.propertyName == value.propertyName)
    );
  }

  revertDeselectedItems(currentPageItems: RemissionFromOrderDetailModel[], selectedIds: Set<number>) {
    for (const item of currentPageItems) {
      if (!selectedIds.has(item.id)) {
        this.contributionState.delete(item.id);
      }
    }
  }

  syncPageItemsQuantityRemission(currentPageItems: RemissionFromOrderDetailModel[]) {
    for (const item of currentPageItems) {
      const contribution = this.contributionState.get(item.id) ?? 0;
      const original = this.originalQuantityRemission.get(item.id) ?? 0;
      item.quantityRemission = original + contribution;
    }
  }
}

